const PWA_DISMISS_COOKIE_NAME = 'catfeeder_pwa_banner_dismissed';
const PWA_DISMISS_COOKIE_MAX_AGE_DAYS = 2;
let deferredPrompt = null;
let installButton = null;
let installBanner = null;
let closeButton = null;
let sparkleInterval = null;
let isIOSSafari = false;
let iosPopup = null;

const IOS_SHARE_SVG =
  '<svg width="18" height="20" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M7 1v11M3 5l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M1 9v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

function detectIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS|Chrome/.test(ua);
  return isIOS && isSafari;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
}

function isAndroidChrome() {
  const ua = navigator.userAgent;
  return /Android/i.test(ua) && /Chrome\/\d+/i.test(ua) && !/EdgA|OPR|SamsungBrowser/i.test(ua);
}

function hasPWADismissCookie() {
  return document.cookie.split(';').some((entry) => entry.trim().startsWith(`${PWA_DISMISS_COOKIE_NAME}=`));
}

function setPWADismissCookie() {
  const maxAge = PWA_DISMISS_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${PWA_DISMISS_COOKIE_NAME}=1; max-age=${maxAge}; path=/; samesite=lax`;
}

export function initPWAInstall() {
  installButton = document.getElementById('pwa-install-button');
  installBanner = document.getElementById('pwa-install-banner');
  closeButton = document.getElementById('pwa-install-close');
  isIOSSafari = detectIOSSafari();

  if (isStandalone() || !isAndroidChrome() || hasPWADismissCookie()) {
    hideBanner();
    return;
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    if (!hasPWADismissCookie()) showBanner();
  });

  window.addEventListener('appinstalled', () => {
    hideBanner();
    deferredPrompt = null;
  });

  if (installButton) {
    installButton.addEventListener('click', handleInstallClick);
  }

  if (closeButton) {
    closeButton.addEventListener('click', () => {
      setPWADismissCookie();
      hideBanner();
    });
  }
}

function handleInstallClick() {
  if (isIOSSafari) {
    showIOSPopup();
  } else {
    promptInstall();
  }
}

function createIOSPopup() {
  iosPopup = document.createElement('div');
  iosPopup.className = 'pwa-ios-popup';
  iosPopup.setAttribute('role', 'dialog');
  iosPopup.setAttribute('aria-label', 'Install instructions');
  iosPopup.innerHTML =
    '<div class="pwa-ios-popup-content crt crt-absolute">' +
      '<button class="pwa-ios-popup-close" aria-label="Close">&times;</button>' +
      '<p class="pwa-ios-popup-title"><strong>install the app</strong><br><i>get extra features, notifications and a better user experience</i></p>' +
      '<div class="pwa-ios-popup-steps">' +
        '<div class="pwa-ios-popup-step">' +
          '<span class="pwa-ios-popup-step-num">1.</span>' +
          '<span>Tap the share button <span class="pwa-ios-share-icon">' + IOS_SHARE_SVG + '</span> in Safari</span>' +
        '</div>' +
        '<div class="pwa-ios-popup-step">' +
          '<span class="pwa-ios-popup-step-num">2.</span>' +
          '<span>Find the option <strong>"Add to Home Screen"</strong> and tap it!</span>' +
        '</div>' +
      '</div>' +
    '</div>';

  document.body.appendChild(iosPopup);

  iosPopup.querySelector('.pwa-ios-popup-close').addEventListener('click', hideIOSPopup);
  iosPopup.addEventListener('click', (e) => {
    if (e.target === iosPopup) hideIOSPopup();
  });
}

function showIOSPopup() {
  if (!iosPopup) return;
  iosPopup.classList.add('active');
}

function hideIOSPopup() {
  if (!iosPopup) return;
  iosPopup.classList.remove('active');
}

function updateTabsOffset() {
  if (!installBanner) return;
  const height = installBanner.offsetHeight;
  document.documentElement.style.setProperty('--pwa-banner-height', `${height + 8}px`);
}

function startSparkleAnimation() {
  const chars = ['₊', '˚', '⊹'];
  const perms = [];
  for (let i = 0; i < chars.length; i++)
    for (let j = 0; j < chars.length; j++)
      for (let k = 0; k < chars.length; k++)
        if (i !== j && j !== k && i !== k) perms.push([chars[i], chars[j], chars[k]]);

  const left = document.querySelector('.pwa-sparkle-left');
  const right = document.querySelector('.pwa-sparkle-right');
  if (!left || !right) return;

  let idx = 0;
  sparkleInterval = setInterval(() => {
    idx = (idx + 1) % perms.length;
    const p = perms[idx];
    left.textContent = p.join('');
    right.textContent = [...p].reverse().join('');
  }, 500);
}

function stopSparkleAnimation() {
  if (sparkleInterval) {
    clearInterval(sparkleInterval);
    sparkleInterval = null;
  }
}

function showBanner() {
  if (hasPWADismissCookie()) return;
  if (installBanner) {
    installBanner.style.display = 'flex';
    requestAnimationFrame(updateTabsOffset);
    startSparkleAnimation();
  }
}

function hideBanner() {
  if (installBanner) {
    installBanner.style.display = 'none';
    document.documentElement.style.setProperty('--pwa-banner-height', '0px');
    stopSparkleAnimation();
  }
}

async function promptInstall() {
  if (!deferredPrompt) return;

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;

  if (outcome === 'accepted') {
    hideBanner();
  }

  deferredPrompt = null;
}

export function isInstallable() {
  return deferredPrompt !== null;
}

export function refreshBannerLayout() {
  if (installBanner && installBanner.style.display !== 'none') {
    requestAnimationFrame(updateTabsOffset);
  }
}

export function hideBannerWhenLeavingLanding() {
  hideBanner();
}
